import React from "react";
import mapboxgl from "mapbox-gl";
import Compare from 'mapbox-gl-compare';
import 'mapbox-gl-compare/dist/mapbox-gl-compare.css';
import { GeoJSONFeatureCollection } from "../types";
import { initializeMap, addRoutesLayer, addSchoolsLayer } from "../utils/mapUtils";

// Type definitions
interface MapProps {
  fetchedRoutes: GeoJSONFeatureCollection | null;
  fetchedSchools: GeoJSONFeatureCollection | null;
}

interface MapContainerProps {
  width: string;
  height: string;
}

interface CompareOptions {
  mousemove: boolean;
  orientation: 'vertical' | 'horizontal';
}

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN || "";

// Hook for map initialization and comparison
const useMapCompare = (
  mapContainerRef: React.RefObject<HTMLDivElement>,
  leftContainerRef: React.RefObject<HTMLDivElement>,
  rightContainerRef: React.RefObject<HTMLDivElement>
) => {
  const leftMapRef = React.useRef<mapboxgl.Map | null>(null);
  const rightMapRef = React.useRef<mapboxgl.Map | null>(null);
  const compareRef = React.useRef<Compare | null>(null);

  const initializeCompare = React.useCallback((
    leftMap: mapboxgl.Map,
    rightMap: mapboxgl.Map,
    container: HTMLDivElement
  ) => {
    const compareOptions: CompareOptions = {
      mousemove: false,
      orientation: 'vertical'
    };

    compareRef.current = new Compare(
      leftMap,
      rightMap,
      container,
      compareOptions
    );
  }, []);

  const handleMapLoad = React.useCallback((
    leftMap: mapboxgl.Map,
    rightMap: mapboxgl.Map,
    container: HTMLDivElement
  ) => {
    if (leftMap.isStyleLoaded() && rightMap.isStyleLoaded()) {
      initializeCompare(leftMap, rightMap, container);
    } else {
      const initWhenReady = () => {
        if (leftMap.isStyleLoaded() && rightMap.isStyleLoaded()) {
          initializeCompare(leftMap, rightMap, container);
        }
      };

      leftMap.once('style.load', initWhenReady);
      rightMap.once('style.load', initWhenReady);
    }
  }, [initializeCompare]);

  const initMap = React.useCallback(() => {
    if (leftMapRef.current || !leftContainerRef.current || !rightContainerRef.current || !mapContainerRef.current) return;

    leftMapRef.current = initializeMap(leftContainerRef.current);
    rightMapRef.current = initializeMap(rightContainerRef.current);

    const container = mapContainerRef.current;

    leftMapRef.current.on('load', () => {
      if (rightMapRef.current?.loaded()) {
        handleMapLoad(leftMapRef.current!, rightMapRef.current, container);
      }
    });

    rightMapRef.current.on('load', () => {
      if (leftMapRef.current?.loaded()) {
        handleMapLoad(leftMapRef.current, rightMapRef.current!, container);
      }
    });
  }, [handleMapLoad, leftContainerRef, rightContainerRef, mapContainerRef]);

  return { leftMapRef, rightMapRef, initMap };
};

// Main component
const Map: React.FC<MapProps> = ({ fetchedRoutes, fetchedSchools }) => {
  const mapContainerRef = React.useRef<HTMLDivElement>(null);
  const leftContainerRef = React.useRef<HTMLDivElement>(null);
  const rightContainerRef = React.useRef<HTMLDivElement>(null);

  const { leftMapRef, rightMapRef, initMap } = useMapCompare(
    mapContainerRef,
    leftContainerRef,
    rightContainerRef
  );

  React.useEffect(() => {
    initMap();
  }, [initMap]);

  React.useEffect(() => {
    if (!leftMapRef.current || !rightMapRef.current) return;

    const maps = [leftMapRef.current, rightMapRef.current];
    maps.forEach((map, index) => {
      addRoutesLayer(map, fetchedRoutes, index === 0 ? 'left' : 'right');
    });
  }, [fetchedRoutes]);

  React.useEffect(() => {
    if (!leftMapRef.current || !rightMapRef.current) return;

    const maps = [leftMapRef.current, rightMapRef.current];
    maps.forEach(map => {
      addSchoolsLayer(map, fetchedSchools);
    });
  }, [fetchedSchools]);

  const containerStyle: MapContainerProps = {
    width: '100%',
    height: '100vh'
  };

  return (
    <div ref={mapContainerRef} style={{ position: 'relative', ...containerStyle }}>
      <div ref={leftContainerRef} style={{ position: 'absolute', top: 0, bottom: 0, width: '100%' }} />
      <div ref={rightContainerRef} style={{ position: 'absolute', top: 0, bottom: 0, width: '100%' }} />
    </div>
  );
};

export default Map;
